import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../../api.js";
import { useAuth } from "../../context/AuthContext.jsx";

const ForumContext = createContext();

export function ForumProvider({ children }) {
  const { token, isLoggedIn } = useAuth();
  const [membership, setMembership]   = useState(undefined); // undefined = loading
  const [categories, setCategories]   = useState([]);
  const [stats, setStats]             = useState({ threads: 0, posts: 0, members: 0 });

  const headers = useCallback(() =>
    token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const loadMembership = useCallback(async () => {
    if (!isLoggedIn) { setMembership(null); return; }
    try {
      const { data } = await axios.get(`${API_BASE}/api/forum/membership`, { headers: headers() });
      setMembership(data);
    } catch {
      setMembership(null);
    }
  }, [isLoggedIn, headers]);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/forum/categories`);
      setCategories(data);
    } catch {
      setCategories([]);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/api/forum/stats`);
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => {
    loadMembership();
    loadCategories();
    loadStats();
  }, [loadMembership, loadCategories, loadStats]);

  const register = useCallback(async (disciplines, notifyReplies, notifyMentions) => {
    const { data } = await axios.post(
      `${API_BASE}/api/forum/register`,
      { disciplines, notifyReplies, notifyMentions },
      { headers: headers() }
    );
    setMembership(data);
    return data;
  }, [headers]);

  const isPending = !!membership && membership.isApproved === false;
  const isMember  = !!membership && !membership.isBanned && !isPending;
  const isMod     = isMember && (membership.role === "moderator" || membership.role === "admin");

  return (
    <ForumContext.Provider value={{
      membership, isMember, isMod, isPending,
      categories, stats,
      loadMembership, loadCategories, loadStats,
      register, headers,
    }}>
      {children}
    </ForumContext.Provider>
  );
}

export const useForum = () => useContext(ForumContext);
